import { create } from 'zustand';

export type AsciiSpeed = 'slow' | 'normal' | 'fast';
export type AsciiStrength = 'soft' | 'normal' | 'bright';
export type BuiltinPattern = 'waves' | 'rain' | 'ripples' | 'aurora' | 'stars' | 'vortex';
/** Вбудований візерунок або свій фон із файлу: `custom:<id>`. */
export type AsciiPattern = BuiltinPattern | `custom:${string}`;

/** Свій фон: HTML-файл, імпортований у налаштуваннях; назва береться з імені файлу. */
export interface CustomBg {
  id: string;
  name: string;
  html: string;
}

/** Скільки символів одного свого фону зберігаємо: localStorage невеликий. */
export const CUSTOM_HTML_MAX = 200_000;
export const isCustomPattern = (p: string): p is `custom:${string}` => p.startsWith('custom:');

/** Візерунки фону в порядку показу в налаштуваннях; перший — звичний. */
export const ASCII_PATTERNS: { value: BuiltinPattern; name: string; sample: string }[] = [
  { value: 'waves', name: 'Хвилі', sample: '~=*#*=~' },
  { value: 'rain', name: 'Дощ', sample: ': | # | :' },
  { value: 'ripples', name: 'Кола на воді', sample: '( (o) )' },
  { value: 'aurora', name: 'Сяйво', sample: '`^*%*^`' },
  { value: 'stars', name: 'Зорі', sample: '.  *  · +' },
  { value: 'vortex', name: 'Вир', sample: '@%*:.' },
];

/** Множник часу хвиль: «звичайна» — повільна, спокійна. */
export const ASCII_SPEED: Record<AsciiSpeed, number> = { slow: 0.15, normal: 0.3, fast: 0.6 };
/** Найбільша непрозорість символу. */
export const ASCII_ALPHA: Record<AsciiStrength, number> = { soft: 0.15, normal: 0.3, bright: 0.5 };

type Keys = 'on' | 'speed' | 'strength' | 'pattern' | 'glass' | 'customs';

interface AsciiState {
  on: boolean;
  speed: AsciiSpeed;
  strength: AsciiStrength;
  pattern: AsciiPattern;
  /** Скло плиток: false — щільні панелі без розмиття (легше для слабкого комп'ютера). */
  glass: boolean;
  /** Імпортовані свої фони. */
  customs: CustomBg[];
  /** Збережене на пристрої: від нього рахується «Зміни не збережено». */
  saved: Pick<AsciiState, Keys>;
  /** Показати зміну одразу, але не зберігати — до «Зберегти» в налаштуваннях. */
  set: (p: Partial<Pick<AsciiState, Keys>>) => void;
  commit: () => void;
  revert: () => void;
}

const pick = ({ on, speed, strength, pattern, glass, customs }: Pick<AsciiState, Keys>): Pick<AsciiState, Keys> => ({
  on,
  speed,
  strength,
  pattern,
  glass,
  customs,
});

/** Чи відрізняється показане від збереженого. */
export const asciiDirty = (s: AsciiState): boolean => JSON.stringify(pick(s)) !== JSON.stringify(pick(s.saved));

const KEY = 'kanc.ascii';

/** Налаштування лише цього пристрою: слабкий комп'ютер може вимкнути ефект. */
const read = (): Pick<AsciiState, Keys> => {
  const base = { on: true, speed: 'normal', strength: 'normal', pattern: 'waves', glass: true, customs: [] as CustomBg[] } as const;
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Pick<AsciiState, Keys>>;
    const customs = Array.isArray(raw.customs)
      ? raw.customs.filter(
          (c): c is CustomBg =>
            !!c && typeof c.id === 'string' && typeof c.name === 'string' && typeof c.html === 'string',
        )
      : [];
    const known =
      typeof raw.pattern === 'string' &&
      (ASCII_PATTERNS.some(p => p.value === raw.pattern) ||
        (isCustomPattern(raw.pattern) && customs.some(c => `custom:${c.id}` === raw.pattern)));
    return {
      customs,
      pattern: known ? raw.pattern! : base.pattern,
      on: raw.on !== false,
      glass: raw.glass !== false,
      speed: raw.speed && raw.speed in ASCII_SPEED ? raw.speed : base.speed,
      strength: raw.strength && raw.strength in ASCII_ALPHA ? raw.strength : base.strength,
    };
  } catch {
    return { ...base };
  }
};

/** Без скла стилі беруть щільні панелі й прибирають розмиття (див. tokens.css). */
const applyGlass = (glass: boolean): void => {
  if (typeof document === 'undefined') return;
  if (glass) delete document.documentElement.dataset.glass;
  else document.documentElement.dataset.glass = 'off';
};

// Порядок полів у pick() сталий: інакше JSON збереженого й показаного
// розходився б навіть без змін, і «Зберегти» з'являлося б після кожного входу.
const initial = pick(read());

export const useAscii = create<AsciiState>((set, get) => ({
  ...initial,
  saved: initial,
  set: p => {
    set(p);
    applyGlass(get().glass);
  },
  commit: () => {
    const now = pick(get());
    set({ saved: now });
    try {
      localStorage.setItem(KEY, JSON.stringify(now));
    } catch {
      // Сховище закрите — налаштування діє до перезавантаження.
    }
  },
  revert: () => {
    set({ ...get().saved });
    applyGlass(get().glass);
  },
}));

applyGlass(useAscii.getState().glass);
